import { PurchasableProduct } from '@accedo/one-plugin-sdk';
import { useCallback, useEffect, useState } from 'react';

import { useRemoveQueryStringParams } from '#/hooks/useRemoveQueryStringParams';
import { authActions } from '#/store/auth/auth.store';
import { usePaywallStore } from '#/store/paywall/paywall.store';

import { SubscriptionParams } from '../PlanPurchaseFlow.types';

export interface SubscribeWithoutPaymentProps {
  selectedProduct: PurchasableProduct;
  voucherCode?: string;
  isPaymentDetailsRequired: boolean;
  onPaymentComplete: (result: {
    isSuccessful: boolean;
    isExternal?: boolean;
  }) => void;
  voucherRedeemed?: boolean;
  onVoucherRedeemed?: () => void;
  giftCode?: string;
}

export const useSubscribeWithoutPayment = ({
  selectedProduct,
  voucherCode,
  isPaymentDetailsRequired,
  onPaymentComplete,
  voucherRedeemed,
  onVoucherRedeemed,
  giftCode,
}: SubscribeWithoutPaymentProps) => {
  const { paywallApi } = usePaywallStore();
  const { removeQueryParams } = useRemoveQueryStringParams();

  const [isLoading, setIsLoading] = useState(false);
  const [
    enableSubscribeWithoutPaymentDetails,
    setEnableSubscribeWithoutPaymentDetails,
  ] = useState(false);

  useEffect(() => {
    setEnableSubscribeWithoutPaymentDetails(!isPaymentDetailsRequired);
  }, [isPaymentDetailsRequired, selectedProduct, voucherCode]);

  const onStartSubscriptionWithoutPaymentDetails = useCallback(async () => {
    if (!paywallApi.completeFreePurchase) {
      return;
    }

    setIsLoading(true);

    if (!voucherRedeemed && giftCode) {
      try {
        await paywallApi.redeemGift?.(giftCode);
        onVoucherRedeemed?.();
      } catch (error) {
        setIsLoading(false);
        throw error;
      }
    }

    try {
      const { isSuccess } = await paywallApi.completeFreePurchase(
        selectedProduct.id,
        { voucherCode }
      );

      await authActions.refreshProfileAfterEntitlementPurchase();

      onPaymentComplete({ isSuccessful: isSuccess, isExternal: false });
      setIsLoading(false);

      removeQueryParams([
        SubscriptionParams.Voucher,
        SubscriptionParams.Product,
      ]);

      return isSuccess;
    } catch (err) {
      setIsLoading(false);
      throw err;
    }
  }, [
    onPaymentComplete,
    paywallApi,
    removeQueryParams,
    selectedProduct,
    voucherCode,
    voucherRedeemed,
    onVoucherRedeemed,
    giftCode,
  ]);

  return {
    isLoading,
    enableSubscribeWithoutPaymentDetails,
    onStartSubscriptionWithoutPaymentDetails,
  };
};
